import {
  POLICY_BOUNDS,
  PRESET_PARAMS,
  type ManagementPolicy,
  type ManagementPreset,
  type ManagementTrigger,
  type PolicyParamKey,
} from "@app/shared/policy";
import { Input } from "@/components/ui/input";
import { Select } from "@/components/ui/select";
import { CheckboxRow } from "@/components/ui/checkbox";
import { RulesPreview } from "@/components/RulesPreview";

/**
 * The decision Andy is actually making: how a trade is managed once it starts paying.
 *
 * The raw `RuleSetEditor` asks "which action fires on TP2_HIT", which is a different and much
 * worse question — it is the grammar, not the choice. This card asks the choice and shows the
 * grammar it produces underneath, live, by running the compiler.
 *
 * **Every preset's parameters stay in state even while hidden.** `PRESET_PARAMS` decides which
 * inputs render, and it is the same table `inferPolicy` uses to decide which parameters mean
 * anything — so half_risk → be_buffer → half_risk round-trips without losing the R value
 * someone typed. Only the parameters a preset reads reach the compiler.
 *
 * These presets are hand-chosen defaults from `docs/fable-5-post-t1-management.md`, not fitted
 * numbers. Nothing here ranks them: that is B1's replay engine, which does not exist, and a
 * card that implied otherwise would be the same overclaim this project keeps auditing out.
 */

const PRESET_LABELS: Record<ManagementPreset, { title: string; blurb: string }> = {
  hold: {
    title: "Hold — nothing after entry",
    blurb: "The stop stays exactly where the signal put it, all the way to TP or SL.",
  },
  be_at_tp1: {
    title: "Breakeven at TP1",
    blurb: "When TP1 pays, the stop moves to the entry price. Simple, and easy to tag on a retest.",
  },
  be_buffer: {
    title: "Breakeven at TP1, with a spread buffer",
    blurb:
      "Breakeven held back by a multiple of the current spread, so an ordinary retest or a spread spike cannot tag it.",
  },
  half_risk: {
    title: "Cut the risk at TP1",
    blurb: "Move the stop part of the way back rather than all the way — you stay in the trade with less at stake.",
  },
  ladder: {
    title: "Ladder (the default)",
    blurb: "Breakeven-plus-buffer when TP1 pays, then the stop follows up to TP1's level when TP2 pays.",
  },
  custom: {
    title: "Custom",
    blurb: "Edited as raw rules in Advanced mode.",
  },
};

const SELECTABLE: ManagementPreset[] = ["hold", "be_at_tp1", "be_buffer", "half_risk", "ladder"];

const PARAM_LABELS: Record<PolicyParamKey, { label: string; hint: string; step: string }> = {
  beOffsetPips: {
    label: "Offset from entry (pips)",
    hint: "Positive locks in profit, negative leaves a little risk. 0 is the entry itself.",
    step: "0.1",
  },
  bufferSpreads: {
    label: "Buffer (× current spread)",
    hint: "Held on the losing side, so a normal retest does not tag the stop.",
    step: "0.5",
  },
  stopAtR: {
    label: "Stop at (R)",
    hint: "−1 leaves the original stop, 0 is breakeven, +0.5 banks half the risk.",
    step: "0.1",
  },
};

const TRIGGERS: { value: ManagementTrigger; label: string }[] = [
  { value: "TP1_HIT", label: "when TP1 pays" },
  { value: "TP2_HIT", label: "when TP2 pays" },
];

export function ManagementPolicyCard({
  value,
  onChange,
  disabled,
}: {
  value: ManagementPolicy;
  onChange: (next: ManagementPolicy) => void;
  disabled?: boolean;
}) {
  const params = PRESET_PARAMS[value.preset];
  // Under `ladder` the TP2 step IS the preset, so the checkbox shows checked and disabled
  // rather than absent: hiding it would make the ladder look like it does less than it does.
  const tp2Forced = value.preset === "ladder";

  return (
    <div className="space-y-4">
      <div>
        <label className="mb-1 block text-xs text-muted-foreground">After TP1</label>
        <Select
          value={value.preset}
          disabled={disabled}
          onChange={(e) => onChange({ ...value, preset: e.target.value as ManagementPreset })}
        >
          {SELECTABLE.map((preset) => (
            <option key={preset} value={preset}>
              {PRESET_LABELS[preset].title}
            </option>
          ))}
        </Select>
        <p className="mt-1 text-xs text-muted-foreground">{PRESET_LABELS[value.preset].blurb}</p>
      </div>

      {params.length > 0 && (
        <div className="grid gap-3 sm:grid-cols-2">
          {params.map((key) => (
            <NumberField
              key={key}
              label={PARAM_LABELS[key].label}
              hint={PARAM_LABELS[key].hint}
              step={PARAM_LABELS[key].step}
              value={value[key]}
              disabled={disabled}
              min={POLICY_BOUNDS[key].min}
              max={POLICY_BOUNDS[key].max}
              onChange={(n) => onChange({ ...value, [key]: n })}
            />
          ))}
        </div>
      )}

      <div className="space-y-2 border-t border-border pt-3">
        <CheckboxRow
          label="Follow the stop up to TP1's level when TP2 pays"
          description={tp2Forced ? "The ladder is this step — it is always on for that preset." : undefined}
          checked={tp2Forced || value.trailToTp1AfterTp2}
          disabled={disabled || tp2Forced}
          onChange={(checked) => onChange({ ...value, trailToTp1AfterTp2: checked })}
        />

        <CheckboxRow
          label="Take a partial profit"
          description="Unverified on this broker — it stays off until the smoke test on the demo account says otherwise."
          checked={value.partialClose.enabled}
          disabled={disabled}
          onChange={(checked) => onChange({ ...value, partialClose: { ...value.partialClose, enabled: checked } })}
        />
        {value.partialClose.enabled && (
          <div className="ml-8 grid gap-3 sm:grid-cols-2">
            <NumberField
              label="Percent to close"
              hint="Of what is still open at that point."
              step="1"
              value={value.partialClose.pct}
              disabled={disabled}
              min={POLICY_BOUNDS.partialPct.min}
              max={POLICY_BOUNDS.partialPct.max}
              onChange={(n) => onChange({ ...value, partialClose: { ...value.partialClose, pct: n } })}
            />
            <TriggerField
              label="Take it"
              value={value.partialClose.at}
              disabled={disabled}
              onChange={(at) => onChange({ ...value, partialClose: { ...value.partialClose, at } })}
            />
          </div>
        )}

        <CheckboxRow
          label="Hand the stop to a server-side trailing stop"
          description="Runs on MetaApi's servers, so it keeps working even while this app is down."
          checked={value.trailing.enabled}
          disabled={disabled}
          onChange={(checked) => onChange({ ...value, trailing: { ...value.trailing, enabled: checked } })}
        />
        {value.trailing.enabled && (
          <div className="ml-8 grid gap-3 sm:grid-cols-2">
            <NumberField
              label="Distance (pips)"
              hint="Our pips, not MetaApi's — a gold pip here is what your channels mean by one."
              step="1"
              value={value.trailing.distancePips}
              disabled={disabled}
              min={POLICY_BOUNDS.trailingDistancePips.min}
              max={POLICY_BOUNDS.trailingDistancePips.max}
              onChange={(n) => onChange({ ...value, trailing: { ...value.trailing, distancePips: n } })}
            />
            <TriggerField
              label="Start it"
              value={value.trailing.at}
              disabled={disabled}
              onChange={(at) => onChange({ ...value, trailing: { ...value.trailing, at } })}
            />
          </div>
        )}
      </div>

      <div className="space-y-2 border-t border-border pt-3">
        <div className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
          When the channel says the signal is over
        </div>
        <CheckboxRow
          label="Cancel any entry that has not filled"
          checked={value.cancelPendingOnSignalClose}
          disabled={disabled}
          onChange={(checked) => onChange({ ...value, cancelPendingOnSignalClose: checked })}
        />
        <CheckboxRow
          label="Close whatever is still open"
          description="Off by default: a channel calling a signal finished is not always a reason to give up the runner."
          checked={value.closeRemainingOnSignalClose}
          disabled={disabled}
          onChange={(checked) => onChange({ ...value, closeRemainingOnSignalClose: checked })}
        />
      </div>

      <RulesPreview policy={value} />
    </div>
  );
}

function TriggerField({
  label,
  value,
  disabled,
  onChange,
}: {
  label: string;
  value: ManagementTrigger;
  disabled?: boolean;
  onChange: (next: ManagementTrigger) => void;
}) {
  return (
    <div>
      <label className="mb-1 block text-xs text-muted-foreground">{label}</label>
      <Select value={value} disabled={disabled} onChange={(e) => onChange(e.target.value as ManagementTrigger)}>
        {TRIGGERS.map((trigger) => (
          <option key={trigger.value} value={trigger.value}>
            {trigger.label}
          </option>
        ))}
      </Select>
    </div>
  );
}

function NumberField({
  label,
  hint,
  step,
  value,
  min,
  max,
  disabled,
  onChange,
}: {
  label: string;
  hint: string;
  step: string;
  value: number;
  min: number;
  max: number;
  disabled?: boolean;
  onChange: (next: number) => void;
}) {
  return (
    <div>
      <label className="mb-1 block text-xs text-muted-foreground">{label}</label>
      <Input
        type="number"
        step={step}
        min={min}
        max={max}
        value={value}
        disabled={disabled}
        onChange={(e) => {
          const next = Number(e.target.value);
          // A half-typed "-" or an empty box is not a number; keeping the last good value is
          // better than writing NaN into the policy and failing the save with a zod error.
          if (Number.isFinite(next)) onChange(next);
        }}
      />
      <p className="mt-1 text-xs text-muted-foreground">{hint}</p>
    </div>
  );
}
